import { User, AuthError } from '@supabase/supabase-js';
import { isJwtExpired } from '@xer/web-shared';
import { supabase } from './supabase';
import {
  AuthState,
  AuthEventType,
  AuthEvent,
  AuthMachineContext,
  AuthPublicState,
  getNextState,
} from './authTransitions';
import { fetchUserProfile } from './authProfileFetch';
import { createAuthStateListener } from './authStateListener';

// Re-export User type from Supabase
export type { User };

// Re-export the auth state/event vocabulary so existing call sites keep
// importing them from this module (the canonical entry point).
export type { AuthEvent, AuthPublicState, UserProfile } from './authTransitions';
export { AuthState, AuthEventType } from './authTransitions';

/**
 * Re-export of the shared `isJwtExpired` helper under the legacy name so
 * existing dashboard-frontend call sites don't need to change. Implementation
 * lives in `@xer/web-shared/jwt`.
 */
export const isTokenExpired = isJwtExpired;

export class AuthStateMachine {
  private state: AuthState = AuthState.INITIALIZING;
  private context: AuthMachineContext = {
    user: null,
    profile: null,
    session: null,
    error: null,
  };
  private listeners: Array<(publicState: AuthPublicState) => void> = [];
  private authStateListener: (() => void) | null = null;
  private destroyed = false;
  private activeAbortController: AbortController | null = null;
  private retryDelayTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Auto-start initialization
    setTimeout(() => this.handleInitialize(), 0);
    // Set up auth state change listener
    this.setupAuthStateListener();
  }

  private log(_message: string) {
    // Auth logging disabled for production
  }

  private error(message: string, error?: unknown) {
    console.error(`🔐 Auth Error: ${message}`, error);
  }

  private setupAuthStateListener() {
    this.authStateListener = createAuthStateListener({
      context: this.context,
      getState: () => this.state,
      fetchUserProfile: (userId) => this.fetchUserProfile(userId),
      transitionTo: (newState) => this.transitionTo(newState),
      notifyListeners: () => this.notifyListeners(),
      log: (message) => this.log(message),
      error: (message, error) => this.error(message, error),
    });
  }

  // Clean up listener and cancel in-flight retries when machine is destroyed
  destroy() {
    this.destroyed = true;
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
    if (this.retryDelayTimer) {
      clearTimeout(this.retryDelayTimer);
      this.retryDelayTimer = null;
    }
    if (this.authStateListener) {
      this.authStateListener();
      this.authStateListener = null;
    }
  }

  getDebugInfo() {
    return {
      state: this.state,
      isAuthenticated: this.state === AuthState.AUTHENTICATED,
      user: this.context.user || null,
      profile: this.context.profile || null,
    };
  }

  subscribe(listener: (publicState: AuthPublicState) => void): () => void {
    this.listeners.push(listener);
    // Immediately call with current state
    listener(this.getPublicState());

    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    const publicState = this.getPublicState();
    this.listeners.forEach((listener) => {
      try {
        listener(publicState);
      } catch (error) {
        this.error('Error in state listener', error);
      }
    });
  }

  async send(event: AuthEvent): Promise<void> {
    await this.processEvent(event);
  }

  private async processEvent(event: AuthEvent): Promise<void> {
    const nextState = getNextState(this.state, event.type);
    if (!nextState) {
      return;
    }

    // Transition to new state
    this.state = nextState;
    this.log(`${this.state}`);

    // Execute entry actions for new state
    await this.executeEntryActions(nextState, event);

    // Notify listeners
    this.notifyListeners();
  }

  private async executeEntryActions(state: AuthState, event: AuthEvent): Promise<void> {
    switch (state) {
      case AuthState.INITIALIZING:
        await this.handleInitialize();
        break;
      case AuthState.AUTHENTICATING:
        if (event.type === AuthEventType.LOGIN) {
          await this.handleLogin(event.payload);
        }
        break;
      case AuthState.DEAUTHENTICATING:
        await this.handleLogout(
          event.type === AuthEventType.LOGOUT ? event.payload?.scope : undefined
        );
        break;
      case AuthState.ERROR:
        if (event.type === AuthEventType.LOGIN_FAILED && event.payload?.error) {
          this.context.error = new Error(event.payload.error);
        } else {
          this.context.error = new Error('Unknown error');
        }
        break;
      case AuthState.UNAUTHENTICATED:
        this.context.user = null;
        this.context.profile = null;
        this.context.session = null;
        // Don't clear error here - it may contain a pending approval message
        // Error is cleared when user starts a new login attempt
        break;
    }
  }

  private async handleInitialize(): Promise<void> {
    try {
      this.log('Starting auth initialization...');
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        this.error('Supabase session error during initialization', error);
        this.transitionTo(AuthState.UNAUTHENTICATED);
        return;
      }

      if (!session?.user) {
        this.log('No active session found during initialization');
        this.transitionTo(AuthState.UNAUTHENTICATED);
        return;
      }

      this.log(`Found active session for user: ${session.user.email}`);
      this.context.session = session;
      this.context.user = session.user;

      // Try to load profile - fail authentication if profile cannot be loaded
      this.log('Attempting to load user profile...');
      const profileLoaded = await this.fetchUserProfile(session.user.id);
      if (profileLoaded) {
        // Check if user has an organization assigned
        if (!this.context.profile?.organization_id) {
          // User is pending approval - sign them out and redirect
          this.log('User pending approval (no organization) - signing out');
          await supabase.auth.signOut();
          this.context.user = null;
          this.context.profile = null;
          this.context.session = null;
          this.transitionTo(AuthState.UNAUTHENTICATED);
          window.location.href = '/pending-approval';
          return;
        }
        this.log('Profile loaded successfully, transitioning to AUTHENTICATED');
        this.transitionTo(AuthState.AUTHENTICATED);
      } else {
        this.error(
          'Profile loading failed during initialization - cannot authenticate without profile'
        );
        this.transitionTo(AuthState.UNAUTHENTICATED);
      }
    } catch (error) {
      this.error('Initialize failed', error);
      this.transitionTo(AuthState.UNAUTHENTICATED);
    }
  }

  private transitionTo(newState: AuthState): void {
    this.state = newState;
    this.log(`${newState}`);
    this.notifyListeners();
  }

  private async handleLogin(credentials: { email: string; password: string }): Promise<void> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword(credentials);

      if (error || !data.session?.user) {
        await this.send({
          type: AuthEventType.LOGIN_FAILED,
          payload: { error: error?.message || 'Login failed' },
        });
        return;
      }

      this.context.session = data.session;
      this.context.user = data.session.user;

      // Try to load profile
      const profileLoaded = await this.fetchUserProfile(data.session.user.id);
      if (profileLoaded) {
        // Check if user has an organization assigned
        if (!this.context.profile?.organization_id) {
          // User is pending approval - sign them out and redirect
          await supabase.auth.signOut();
          this.context.user = null;
          this.context.profile = null;
          this.context.session = null;
          await this.send({
            type: AuthEventType.LOGIN_FAILED,
            payload: { error: 'PENDING_APPROVAL' },
          });
          window.location.href = '/pending-approval';
          return;
        }
        await this.send({ type: AuthEventType.LOGIN_SUCCESS });
      } else {
        await this.send({
          type: AuthEventType.LOGIN_FAILED,
          payload: { error: 'Failed to load user profile' },
        });
      }
    } catch (error) {
      this.error('Login error', error);
      await this.send({
        type: AuthEventType.LOGIN_FAILED,
        payload: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }
  }

  private async handleLogout(scope: 'global' | 'local' = 'global'): Promise<void> {
    try {
      // `local` (passed only for the explicit user-initiated sign-out, see
      // AuthContextV2.signOut) revokes just this browser session. The default
      // `global` scope revokes every session for the user, including the one
      // the xer-knowledge MCP connector (ChatGPT/claude.ai) holds — correct
      // for the other LOGOUT triggers (locally-expired-token / API-401), and
      // for the unrelated pending-approval `signOut()` calls elsewhere in this
      // file, which really should kill every session — but wrong for "the
      // user clicked Sign Out in this tab" (#1348).
      await supabase.auth.signOut({ scope });
    } catch (error) {
      this.error('Logout error', error);
    }

    // Always force logout regardless of Supabase response
    localStorage.removeItem('supabase.auth.token');
    sessionStorage.clear();

    await this.send({ type: AuthEventType.LOGOUT_SUCCESS });
  }

  private fetchUserProfile(userId: string): Promise<boolean> {
    return fetchUserProfile(userId, {
      context: this.context,
      isDestroyed: () => this.destroyed,
      setActiveAbortController: (controller) => {
        this.activeAbortController = controller;
      },
      setRetryDelayTimer: (timer) => {
        this.retryDelayTimer = timer;
      },
      log: (message) => this.log(message),
      error: (message, error) => this.error(message, error),
    });
  }

  getPublicState(): AuthPublicState {
    return {
      isAuthenticated: this.state === AuthState.AUTHENTICATED,
      isLoading: [
        AuthState.INITIALIZING,
        AuthState.AUTHENTICATING,
        AuthState.DEAUTHENTICATING,
      ].includes(this.state),
      user: this.context.user,
      profile: this.context.profile,
      error: this.context.error,
    };
  }

  // Auth helper methods
  async login(email: string, password: string): Promise<{ error: AuthError | null }> {
    try {
      // Clear any previous error when starting a new login attempt
      this.context.error = null;
      this.transitionTo(AuthState.AUTHENTICATING);

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error || !data.session?.user) {
        this.transitionTo(AuthState.UNAUTHENTICATED);
        return { error: error || (new Error('Login failed') as AuthError) };
      }

      this.context.session = data.session;
      this.context.user = data.session.user;

      const profileLoaded = await this.fetchUserProfile(data.session.user.id);
      if (profileLoaded) {
        // Check if user has an organization assigned
        if (!this.context.profile?.organization_id) {
          // User is pending approval - sign them out and redirect
          await supabase.auth.signOut();
          this.context.user = null;
          this.context.profile = null;
          this.context.session = null;
          this.transitionTo(AuthState.UNAUTHENTICATED);
          window.location.href = '/pending-approval';
          return { error: new Error('PENDING_APPROVAL') as AuthError };
        }
        this.transitionTo(AuthState.AUTHENTICATED);
        return { error: null };
      } else {
        this.transitionTo(AuthState.UNAUTHENTICATED);
        return { error: new Error('Failed to load user profile') as AuthError };
      }
    } catch (error) {
      this.error('Login error', error);
      this.transitionTo(AuthState.UNAUTHENTICATED);
      return { error: error as AuthError };
    }
  }

  async resetPassword(email: string) {
    try {
      // Send OTP code via email for password reset
      // Using signInWithOtp as a workaround since Supabase doesn't have native OTP password reset
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false, // Don't create new users, only send to existing accounts
        },
      });

      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }

  async verifyPasswordResetOtp(email: string, token: string) {
    try {
      // Verify the OTP code and establish session
      const { error, data } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) {
        return { error };
      }

      // Session is now established, ready for password update
      return { error: null, session: data.session };
    } catch (error) {
      return { error: error as AuthError, session: null };
    }
  }

  async updatePassword(newPassword: string) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }

  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    if (this.context.session?.access_token) {
      headers['Authorization'] = `Bearer ${this.context.session.access_token}`;
    }

    return headers;
  }

  // Add method to expose session for debugging
  getSession() {
    return this.context.session;
  }
}
